import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';

import { Actions as Snack } from '../store/ducks/snacks';
import { mapGeeError, matchGeeError } from '../algorithms/utils';

/**
 * @constant ERROR_NOTICE_PREFIX
 * @description Prefix for the notice's key. A fresh key is built from it every time rather than
 * reusing a fixed one, so the same problem happening twice in a row is announced twice - with one
 * fixed key the second notice is silently dropped as a duplicate.
 * @memberof module:SHARED
 */
const ERROR_NOTICE_PREFIX = 'error-notice';

/**
 * @constant ERROR_NOTICE_MS
 * @description How long a notice stays before it withdraws itself. Longer than the library's own
 * five seconds, because these say more than "saved" and are worth a second read.
 * @memberof module:SHARED
 */
const ERROR_NOTICE_MS = 10000;

/**
 * @function useErrorNotice
 * @description Reports a problem to the user as a snackbar, and turns an Earth Engine failure into
 * something worth reading on the way.
 * Replaces `window.alert`, which the app used everywhere for this: an alert blocks the page until
 * it is dismissed, cannot be styled or stacked, appears detached from what caused it, and is the
 * one dialog a browser is allowed to suppress - on a repeated failure the user simply stops being
 * told. A snackbar sits in the corner, keeps the page usable, and is the pattern the intertidal
 * module already follows.
 * Every notice carries a close button and withdraws itself after {@link ERROR_NOTICE_MS}. The
 * library pauses its own countdown while the pointer rests on the notice or the window loses
 * focus - which is what a file download does - so without a way to close it by hand a notice can
 * outlive what it was about and sit on the screen indefinitely.
 * **Called by:
 * @see {AOIChooser}, {CoastlineAnalysisSteps}, {useBaselineDrawing} - anywhere a step used to alert.
 * @memberof module:SHARED
 * @returns {Function} `notifyError(problem, options)` - `problem` is either a message already
 *  translated or an Error. `options.fallback` is what to say when the error is not one Earth Engine
 *  failures are recognised by: the step's own wording beats a generic line, and a recognised failure
 *  ("limite de requisições atingido") beats both.
 */
export const useErrorNotice = () => {
  const dispatch = useDispatch();
  const [t] = useTranslation();

  return useCallback(
    (problem, { fallback } = {}) => {
      const message =
        typeof problem === 'string' ? problem : matchGeeError(problem, t) || fallback || mapGeeError(problem, t);
      if (!message) return;
      const key = `${ERROR_NOTICE_PREFIX}-${Date.now()}`;
      dispatch(
        Snack.error(key, message, {
          autoHideDuration: ERROR_NOTICE_MS,
          action: () => (
            <IconButton size="small" color="inherit" aria-label="close" onClick={() => dispatch(Snack.dismiss(key))}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ),
        }),
      );
    },
    [dispatch, t],
  );
};

export default useErrorNotice;
